<template>
  <div class="chatbot-icon-wrapper" :class="{ rtl: isArabic }">
    <!-- Mobile Floating Button -->
    <div class="chatbot-mobile-button" @click="openChat = true">
      <div class="chatbot-mobile-label">{{ $t("chatbot.needHelp") }}</div>
      <img src="../assets/chatbot.svg" alt="Chatbot" />
      <div class="chatbot-mobile-pulse"></div>
    </div>

    <!-- Desktop Icon with Label -->
    <div class="chatbot-desktop-icon">
      <div class="chatbot-label">{{ $t("chatbot.needHelp") }}</div>
      <div class="chatbot-icon" @click="openChat = true">
        <img src="@/assets/chatbot.svg" alt="Chatbot" />
      </div>
    </div>

    <!-- Chat Popup -->
    <div
      v-if="openChat"
      class="chatbot-popup"
      :class="{ rtl: isArabic, 'mobile-view': isMobile }"
    >
      <!-- Mobile Header -->
      <div class="chatbot-mobile-header" v-if="isMobile">
        <div class="chatbot-mobile-header-content">
          <div class="chatbot-mobile-info">
            <h3>{{ $t("chatbot.botTitle") }}</h3>
          </div>
        </div>
        <div class="chatbot-mobile-header-actions">
          <button
            class="chatbot-mobile-menu-btn"
            @click="toggleMenu"
            ref="menuBtn"
          >
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#181818"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle cx="12" cy="5" r="1.5" />
              <circle cx="12" cy="12" r="1.5" />
              <circle cx="12" cy="19" r="1.5" />
            </svg>
          </button>
          <button class="chatbot-mobile-close" @click="openChat = false">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        <div v-if="showMenu" class="chatbot-mobile-menu" ref="menu">
          <button class="chatbot-mobile-menu-item" @click="restartChat">
            {{ $t("chatbot.restartChat") }}
          </button>
        </div>
      </div>

      <!-- Desktop Header -->
      <div class="chatbot-popup-header" v-if="!isMobile">
        <div class="chatbot-popup-header-content">
          <div class="chatbot-popup-info">
            <span class="chatbot-popup-title">{{
              $t("chatbot.botTitle")
            }}</span>
          </div>
        </div>
        <div class="chatbot-popup-header-actions">
          <button
            class="chatbot-popup-menu-btn"
            @click="toggleMenu"
            ref="menuBtn"
          >
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#181818"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle cx="12" cy="5" r="1.5" />
              <circle cx="12" cy="12" r="1.5" />
              <circle cx="12" cy="19" r="1.5" />
            </svg>
          </button>
          <button class="chatbot-popup-close" @click="openChat = false">
            &times;
          </button>
        </div>
        <div v-if="showMenu" class="chatbot-popup-menu" ref="menu">
          <button class="chatbot-popup-menu-item" @click="restartChat">
            {{ $t("chatbot.restartChat") }}
          </button>
        </div>
      </div>

      <!-- Chat Body -->
      <div class="chatbot-popup-body">
        <div class="chatbot-popup-messages" ref="messagesContainer">
          <div class="chatbot-popup-welcome">
            {{ $t("chatbot.welcome") }}
          </div>
          <div
            v-for="(msg, idx) in messages"
            :key="idx"
            :class="['chatbot-popup-message', msg.sender]"
          >
            <div v-html="msg.text.replace(/\n/g, '<br>')"></div>
          </div>
          <!-- Typing Indicator -->
          <div
            v-if="loading"
            class="chatbot-popup-message bot typing-indicator"
          >
            <div class="typing-dots">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>
      </div>

      <!-- Suggestions -->
      <div class="chatbot-popup-suggestions" v-if="!isMobile">
        <button
          v-for="(suggestion, i) in suggestions"
          :key="i"
          class="chatbot-popup-suggestion"
          @click="sendSuggestion(suggestion)"
        >
          {{ suggestion }}
        </button>
      </div>

      <!-- Mobile Suggestions -->
      <div class="chatbot-mobile-suggestions" v-if="isMobile">
        <div class="chatbot-mobile-suggestions-scroll">
          <button
            v-for="(suggestion, i) in suggestions"
            :key="i"
            class="chatbot-mobile-suggestion"
            @click="sendSuggestion(suggestion)"
          >
            {{ suggestion }}
          </button>
        </div>
      </div>

      <!-- Input Bar -->
      <div class="chatbot-popup-input-bar">
        <input
          v-model="userInput"
          @keyup.enter="sendMessage"
          type="text"
          :placeholder="$t('chatbot.placeholder')"
          class="chatbot-popup-input"
          :disabled="loading"
          ref="inputElement"
        />
        <button
          class="chatbot-popup-send"
          @click="sendMessage"
          :disabled="loading"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#F5BE32"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <line x1="22" y1="2" x2="11" y2="13"></line>
            <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
          </svg>
        </button>
      </div>
    </div>

    <!-- Mobile Menu Overlay -->
    <div
      v-if="openChat && isMobile"
      class="chatbot-mobile-overlay"
      @click="openChat = false"
    ></div>
  </div>
</template>

<script setup>
import {
  ref,
  nextTick,
  onMounted,
  onBeforeUnmount,
  watch,
  computed,
} from "vue";
import { useI18n } from "vue-i18n";
import {
  clearSmartChatbotMemory,
  refreshSmartChatbotData,
} from "@/utils/hybridChatbot";
import {
  getEnhancedResponse,
  setEnhancedLanguage,
  refreshEnhancedData,
} from "@/utils/enhancedChatbotService";

const { locale } = useI18n();
const isArabic = computed(() => locale.value === "ar");

// Mobile detection
const isMobile = ref(false);

const openChat = ref(false);
const userInput = ref("");
const messages = ref([]);
const showMenu = ref(false);
const loading = ref(false);
const messagesContainer = ref(null);
const menu = ref(null);
const menuBtn = ref(null);
const inputElement = ref(null);

// New conversation flow state management
const conversationPhase = ref('initial'); // 'initial', 'intent_discovery', 'contextual', 'deep_engagement', 'conversion'
const customerIntent = ref(null); // 'menu', 'reservation', 'information', 'delivery'
const customerPreferences = ref({});

// Enhanced conversation context tracking
const conversationContext = ref({
  exploredCategories: [], // Track which categories user has seen
  currentFocus: null,     // What they're currently asking about
  conversationDepth: 0,   // How deep into food exploration they are
  lastResponse: null,     // What the bot just said
  menuExplorationPhase: 'initial', // 'initial', 'exploring_categories', 'ready_to_order', 'ordering'
  contextUpdated: false   // Flag to force suggestion updates
});

const suggestions = ref([
  "Show me the menu",
  "How can I make a reservation?"
]);

// Enhanced conversation flow suggestions
const conversationFlowSuggestions = {
  initial: {
    en: [
      "📋 View Menu",
      "🕐 Opening Hours",
      "📍 Location"
    ],
    ar: [
      "📋 عرض القائمة",
      "🕐 ساعات العمل",
      "📍 الموقع"
    ]
  },
  intent_discovery: {
    en: [
      "📋 View Menu",
      "🕐 Opening Hours"
    ],
    ar: [
      "📋 عرض القائمة",
      "🕐 ساعات العمل"
    ]
  },
  menu_categories: {
    en: [
      "🍣 Sushi & Japanese",
      "🥩 Steaks & Grills",
      "🍗 Chicken Dishes"
    ],
    ar: [
      "🍣 سوشي وياباني",
      "🥩 ستيك وشواية",
      "🍗 أطباق دجاج"
    ]
  },
  reservation_details: {
    en: [
      "📅 Choose date",
      "🕐 Select time",
      "👥 Party size"
    ],
    ar: [
      "📅 اختر التاريخ",
      "🕐 اختر الوقت",
      "👥 عدد الأشخاص"
    ]
  },
  follow_up_questions: {
    menu: {
      en: [
        "What's your favorite cuisine?",
        "Want recommendations?"
      ],
      ar: [
        "ما هو مطبخك المفضل؟",
        "تريد توصيات؟"
      ]
    },
    reservation: {
      en: [
        "How many people?",
        "What time works best?"
      ],
      ar: [
        "كم عدد الأشخاص؟",
        "ما الوقت المناسب؟"
      ]
    },
    information: {
      en: [
        "Need parking info?",
        "Want directions?"
      ],
      ar: [
        "تحتاج معلومات وقوف؟",
        "تريد اتجاهات؟"
      ]
    }
  }
};

// Check if device is mobile
function checkMobile() {
  isMobile.value = window.innerWidth <= 768;
}

// Enhanced function to get suggestions based on conversation phase and context
function getSuggestionsForLanguage() {
  if (conversationPhase.value === 'initial') {
    return isArabic.value ? conversationFlowSuggestions.initial.ar : conversationFlowSuggestions.initial.en;
  }
  
  if (conversationPhase.value === 'intent_discovery') {
    return isArabic.value ? conversationFlowSuggestions.intent_discovery.ar : conversationFlowSuggestions.intent_discovery.en;
  }
  
  if (conversationPhase.value === 'menu_categories' && customerIntent.value === 'menu') {
    return isArabic.value ? conversationFlowSuggestions.menu_categories.ar : conversationFlowSuggestions.menu_categories.en;
  }
  
  if (conversationPhase.value === 'reservation_details' && customerIntent.value === 'reservation') {
    return isArabic.value ? conversationFlowSuggestions.reservation_details.ar : conversationFlowSuggestions.reservation_details.ar;
  }
  
  // Fallback to context-based suggestions
  if (customerIntent.value && conversationFlowSuggestions.follow_up_questions[customerIntent.value]) {
    return isArabic.value ? 
      conversationFlowSuggestions.follow_up_questions[customerIntent.value].ar : 
      conversationFlowSuggestions.follow_up_questions[customerIntent.value].en;
  }
  
  // Default suggestions
  if (isArabic.value) {
    return ["أرني القائمة", "كيف يمكنني الحجز؟"];
  }
  return ["Show me the menu", "How can I make a reservation?"];
}

// Enhanced context-aware suggestion generation with randomization
function generateContextualSuggestions() {
  const { exploredCategories, menuExplorationPhase } = conversationContext.value;
  
  if (isArabic.value) {
    // Arabic suggestions
    if (menuExplorationPhase === 'initial') {
      // Randomize initial menu suggestions
      return generateRandomizedMenuSuggestions('ar');
    }
    
    if (menuExplorationPhase === 'exploring_categories') {
      if (exploredCategories.length === 0) {
        // First time asking - show 3 random categories
        return generateRandomizedMenuSuggestions('ar');
      }
      
      if (exploredCategories.length === 1) {
        // After exploring one category, show remaining categories (exactly 3)
        const remaining = getRemainingCategories(exploredCategories, 'ar');
        return remaining.slice(0, 3);
      }
      
      if (exploredCategories.length === 2) {
        // After exploring two categories, show remaining + ordering hint (exactly 3)
        const remaining = getRemainingCategories(exploredCategories, 'ar');
        return [...remaining, "🛒 أريد الطلب الآن"].slice(0, 3);
      }
      
      if (exploredCategories.length >= 3) {
        // After exploring many categories, guide to ordering (exactly 3)
        return ["🥗 المقبلات", "🥤 المشروبات", "🛒 أريد الطلب الآن"].slice(0, 3);
      }
    }
    
    if (menuExplorationPhase === 'ready_to_order') {
      return ["🛒 طلب أونلاين", "🍽️ حجز طاولة", "📞 طلب عبر الهاتف"].slice(0, 3);
    }
    
    if (menuExplorationPhase === 'ordering') {
      return ["💳 طرق الدفع", "🚚 التوصيل", "📍 الموقع"].slice(0, 3);
    }
  } else {
    // English suggestions
    if (menuExplorationPhase === 'initial') {
      // Randomize initial menu suggestions
      return generateRandomizedMenuSuggestions('en');
    }
    
    if (menuExplorationPhase === 'exploring_categories') {
      if (exploredCategories.length === 0) {
        // First time asking - show 3 random categories
        return generateRandomizedMenuSuggestions('en');
      }
      
      if (exploredCategories.length === 1) {
        // After exploring one category, show remaining categories (exactly 3)
        const remaining = getRemainingCategories(exploredCategories, 'en');
        return remaining.slice(0, 3);
      }
      
      if (exploredCategories.length === 2) {
        // After exploring two categories, show remaining + ordering hint (exactly 3)
        const remaining = getRemainingCategories(exploredCategories, 'en');
        return [...remaining, "🛒 I want to order now"].slice(0, 3);
      }
      
      if (exploredCategories.length >= 3) {
        // After exploring many categories, guide to ordering (exactly 3)
        return ["🥗 Appetizers", "🥤 Beverages", "🛒 I want to order now"].slice(0, 3);
      }
    }
    
    if (menuExplorationPhase === 'ready_to_order') {
      return ["🛒 Order online", "🍽️ Book a table", "📞 Call to order"].slice(0, 3);
    }
    
    if (menuExplorationPhase === 'ordering') {
      return ["💳 Payment methods", "🚚 Delivery", "📍 Location"].slice(0, 3);
    }
  }
  
  // Fallback
  return isArabic.value ? ["أرني القائمة", "كيف يمكنني الحجز؟"] : ["Show me the menu", "How can I make a reservation?"];
}

// Helper function to get remaining unexplored categories with randomization
function getRemainingCategories(exploredCategories, language) {
  const allCategories = language === 'ar' 
    ? ['🍣 السوشي', '🥩 الستيك', '🍗 الدجاج', '🥗 المقبلات', '🥤 المشروبات']
    : ['🍣 Sushi', '🥩 Steaks', '🍗 Chicken', '🥗 Appetizers', '🥤 Beverages'];
  
  // Filter out categories that have already been explored
  const remaining = allCategories.filter(cat => {
    // Check if this category has been explored
    return !exploredCategories.some(explored => {
      // More precise matching to avoid partial matches
      if (language === 'ar') {
        return (cat.includes('سوشي') && explored.includes('سوشي')) ||
               (cat.includes('ستيك') && explored.includes('ستيك')) ||
               (cat.includes('دجاج') && explored.includes('دجاج')) ||
               (cat.includes('مقبلات') && explored.includes('مقبلات')) ||
               (cat.includes('مشروبات') && explored.includes('مشروبات'));
      } else {
        return (cat.includes('Sushi') && explored.includes('sushi')) ||
               (cat.includes('Steaks') && explored.includes('steak')) ||
               (cat.includes('Chicken') && explored.includes('chicken')) ||
               (cat.includes('Appetizers') && explored.includes('appetizer')) ||
               (cat.includes('Beverages') && explored.includes('beverage'));
      }
    });
  });
  
  // Shuffle the remaining categories for variety and return exactly 2
  const shuffled = remaining.sort(() => Math.random() - 0.5);
  return shuffled.slice(0, 2); // Return exactly 2 remaining categories
}

// New function to generate randomized menu suggestions (exactly 3)
function generateRandomizedMenuSuggestions(language) {
  const allCategories = language === 'ar' 
    ? ['🍣 السوشي', '🥩 الستيك', '🍗 الدجاج', '🥗 المقبلات', '🥤 المشروبات']
    : ['🍣 Sushi', '🥩 Steaks', '🍗 Chicken', '🥗 Appetizers', '🥤 Beverages'];
  
  // Shuffle all categories and take exactly 3
  const shuffled = allCategories.sort(() => Math.random() - 0.5);
  return shuffled.slice(0, 3);
}



// Function to advance conversation phase based on user input
function advanceConversationPhase(userMessage) {
  const lowerMessage = userMessage.toLowerCase();
  
  // Phase 1: Initial engagement
  if (conversationPhase.value === 'initial') {
    if (lowerMessage.includes('menu') || lowerMessage.includes('قائمة') || 
        lowerMessage.includes('food') || lowerMessage.includes('طعام')) {
      conversationPhase.value = 'intent_discovery';
      customerIntent.value = 'menu';
      return true;
    }
    if (lowerMessage.includes('reservation') || lowerMessage.includes('حجز') || 
        lowerMessage.includes('book') || lowerMessage.includes('حجز')) {
      conversationPhase.value = 'intent_discovery';
      customerIntent.value = 'reservation';
      return true;
    }
    if (lowerMessage.includes('information') || lowerMessage.includes('معلومات') || 
        lowerMessage.includes('hours') || lowerMessage.includes('ساعات')) {
      conversationPhase.value = 'intent_discovery';
      customerIntent.value = 'information';
      return true;
    }
  }
  
  // Phase 2: Intent discovery
  if (conversationPhase.value === 'intent_discovery') {
    if (customerIntent.value === 'menu') {
      if (lowerMessage.includes('sushi') || lowerMessage.includes('سوشي') ||
          lowerMessage.includes('steak') || lowerMessage.includes('ستيك') ||
          lowerMessage.includes('chicken') || lowerMessage.includes('دجاج')) {
        conversationPhase.value = 'contextual';
        return true;
      }
    }
    if (customerIntent.value === 'reservation') {
      if (lowerMessage.includes('date') || lowerMessage.includes('تاريخ') ||
          lowerMessage.includes('time') || lowerMessage.includes('وقت')) {
        conversationPhase.value = 'reservation_details';
        return true;
      }
    }
  }
  
  // Phase 3: Contextual assistance
  if (conversationPhase.value === 'contextual') {
    if (lowerMessage.includes('ingredient') || lowerMessage.includes('مكونات') ||
        lowerMessage.includes('price') || lowerMessage.includes('سعر')) {
      conversationPhase.value = 'deep_engagement';
      return true;
    }
  }
  
  // Phase 4: Deep engagement
  if (conversationPhase.value === 'deep_engagement') {
    if (lowerMessage.includes('order') || lowerMessage.includes('طلب') ||
        lowerMessage.includes('reserve') || lowerMessage.includes('حجز')) {
      conversationPhase.value = 'conversion';
      return true;
    }
  }
  
  return false;
}

// Enhanced function to update conversation context with dynamic phase detection
function updateConversationContext(userMessage, botResponse) {
  const lowerMessage = userMessage.toLowerCase();
  
  // Update last response
  conversationContext.value.lastResponse = botResponse;
  
  // Detect if user is exploring menu categories
  if (customerIntent.value === 'menu') {
    // Check if user asked about a specific category - this should ALWAYS reset to exploring phase
    if (lowerMessage.includes('sushi') || lowerMessage.includes('سوشي') ||
        lowerMessage.includes('steak') || lowerMessage.includes('ستيك') || lowerMessage.includes('beef') || lowerMessage.includes('لحم') ||
        lowerMessage.includes('chicken') || lowerMessage.includes('دجاج') ||
        lowerMessage.includes('appetizer') || lowerMessage.includes('مقبلات') || lowerMessage.includes('starter') ||
        lowerMessage.includes('beverage') || lowerMessage.includes('مشروب') || lowerMessage.includes('drink') ||
        lowerMessage.includes('dragon') || lowerMessage.includes('creamy') || lowerMessage.includes('salmon') ||
        lowerMessage.includes('wagyu') || lowerMessage.includes('parmesan') || lowerMessage.includes('spring') ||
        lowerMessage.includes('tomato') || lowerMessage.includes('noodle') || lowerMessage.includes('pumpkin') ||
        lowerMessage.includes('panther') || lowerMessage.includes('apple') || lowerMessage.includes('frenchman')) {
      
      // Always reset to exploring phase when asking about food items
      conversationContext.value.menuExplorationPhase = 'exploring_categories';
      
             // Update explored categories and current focus - avoid duplicates
       if (lowerMessage.includes('sushi') || lowerMessage.includes('سوشي')) {
         if (!conversationContext.value.exploredCategories.includes('sushi')) {
           conversationContext.value.exploredCategories.push('sushi');
         }
         conversationContext.value.currentFocus = 'sushi';
       }
       else if (lowerMessage.includes('steak') || lowerMessage.includes('ستيك') || lowerMessage.includes('beef') || lowerMessage.includes('لحم')) {
         if (!conversationContext.value.exploredCategories.includes('steak')) {
           conversationContext.value.exploredCategories.push('steak');
         }
         conversationContext.value.currentFocus = 'steak';
       }
       else if (lowerMessage.includes('chicken') || lowerMessage.includes('دجاج')) {
         if (!conversationContext.value.exploredCategories.includes('chicken')) {
           conversationContext.value.exploredCategories.push('chicken');
         }
         conversationContext.value.currentFocus = 'chicken';
       }
       else if (lowerMessage.includes('appetizer') || lowerMessage.includes('مقبلات') || lowerMessage.includes('starter')) {
         if (!conversationContext.value.exploredCategories.includes('appetizer')) {
           conversationContext.value.exploredCategories.push('appetizer');
         }
         conversationContext.value.currentFocus = 'appetizer';
       }
       else if (lowerMessage.includes('beverage') || lowerMessage.includes('مشروب') || lowerMessage.includes('drink')) {
         if (!conversationContext.value.exploredCategories.includes('beverage')) {
           conversationContext.value.exploredCategories.push('beverage');
         }
         conversationContext.value.currentFocus = 'beverage';
       }
      
      // Force context update to trigger dynamic suggestions
      conversationContext.value.contextUpdated = true;
    }
    
    // Check if user is ready to order (explored 3+ categories or asked about ordering)
    else if (conversationContext.value.exploredCategories.length >= 3 || 
        lowerMessage.includes('order') || lowerMessage.includes('طلب') ||
        lowerMessage.includes('buy') || lowerMessage.includes('شراء') ||
        lowerMessage.includes('how to order') || lowerMessage.includes('كيف أطلب')) {
      conversationContext.value.menuExplorationPhase = 'ready_to_order';
    }
    
    // Check if user is in ordering phase
    else if (lowerMessage.includes('payment') || lowerMessage.includes('دفع') ||
        lowerMessage.includes('delivery') || lowerMessage.includes('توصيل') ||
        lowerMessage.includes('reservation') || lowerMessage.includes('حجز') ||
        lowerMessage.includes('credit card') || lowerMessage.includes('بطاقة ائتمان') ||
        lowerMessage.includes('cash') || lowerMessage.includes('نقدي')) {
      conversationContext.value.menuExplorationPhase = 'ordering';
    }
    
    // Auto-advance to ordering phase if user has explored many categories
    else if (conversationContext.value.exploredCategories.length >= 4) {
      conversationContext.value.menuExplorationPhase = 'ready_to_order';
    }
  }
  
  // Increment conversation depth
  conversationContext.value.conversationDepth++;
}

// Enhanced function to update suggestions based on conversation context
function updateSuggestions(userMessage, botResponse) {
  const lowerMessage = userMessage.toLowerCase();
  const lowerResponse = botResponse.toLowerCase();

  // Advance conversation phase if possible
  advanceConversationPhase(userMessage);
  
  // Update conversation context
  updateConversationContext(userMessage, botResponse);

  // ALWAYS prioritize context-aware suggestions for menu exploration
  if (customerIntent.value === 'menu' && conversationContext.value.menuExplorationPhase !== 'initial') {
    const contextualSuggestions = generateContextualSuggestions();
    if (contextualSuggestions && contextualSuggestions.length > 0) {
      suggestions.value = contextualSuggestions;
      
      // Reset the context updated flag after using it
      conversationContext.value.contextUpdated = false;
      return;
    }
  }
  
  // Force context-aware suggestions if context was updated
  if (conversationContext.value.contextUpdated && customerIntent.value === 'menu') {
    const contextualSuggestions = generateContextualSuggestions();
    if (contextualSuggestions && contextualSuggestions.length > 0) {
      suggestions.value = contextualSuggestions;
      conversationContext.value.contextUpdated = false;
      return;
    }
  }

  // Get phase-appropriate suggestions
  let newSuggestions = getSuggestionsForLanguage();

  // If we have phase-specific suggestions, use them
  if (newSuggestions.length > 0 && newSuggestions[0] !== suggestions.value[0]) {
    suggestions.value = newSuggestions;
    return;
  }

  // Intelligent context-based suggestions that avoid redundancy
  
  // Check if bot just provided a menu response (contains menu categories)
  if (
    lowerResponse.includes("menu categories") ||
    lowerResponse.includes("فئات قائمة") ||
    (lowerResponse.includes("🍣") && lowerResponse.includes("🥩") && lowerResponse.includes("🍗")) ||
    (lowerResponse.includes("سوشي") && lowerResponse.includes("ستيك") && lowerResponse.includes("دجاج")) ||
    lowerResponse.includes("our menu categories") ||
    lowerResponse.includes("فئات قائمة الطعام")
  ) {
    // Show randomized category suggestions after menu response
    if (isArabic.value) {
      newSuggestions = generateRandomizedMenuSuggestions('ar');
    } else {
      newSuggestions = generateRandomizedMenuSuggestions('en');
    }
    suggestions.value = newSuggestions;
    return;
  }
  
  // Check if bot just provided a general menu overview (like "what do you have")
  if (
    lowerResponse.includes("variety of delicious dishes") ||
    lowerResponse.includes("مجموعة متنوعة من الأطباق") ||
    (lowerResponse.includes("🍣") && lowerResponse.includes("🥩") && lowerResponse.includes("🍗") && lowerResponse.includes("🥗")) ||
    (lowerResponse.includes("سوشي") && lowerResponse.includes("ستيك") && lowerResponse.includes("دجاج") && lowerResponse.includes("مقبلات"))
  ) {
    // Show randomized category suggestions after general menu overview
    if (isArabic.value) {
      newSuggestions = generateRandomizedMenuSuggestions('ar');
    } else {
      newSuggestions = generateRandomizedMenuSuggestions('en');
    }
    suggestions.value = newSuggestions;
    return;
  }
  
  // Check if bot just provided specials or popular dishes
  if (
    lowerResponse.includes("today's specials") ||
    lowerResponse.includes("عروض اليوم") ||
    lowerResponse.includes("most popular dishes") ||
    lowerResponse.includes("أكثر أطباقنا شعبية") ||
    lowerResponse.includes("🌟") || lowerResponse.includes("🔥")
  ) {
    // Show randomized category suggestions after specials/popular dishes
    if (isArabic.value) {
      newSuggestions = generateRandomizedMenuSuggestions('ar');
    } else {
      newSuggestions = generateRandomizedMenuSuggestions('en');
    }
    suggestions.value = newSuggestions;
    return;
  }
  
  // Check if bot mentions menu categories in general
  if (
    lowerResponse.includes("menu categories") ||
    lowerResponse.includes("فئات القائمة") ||
    lowerResponse.includes("sushi, steak, chicken, appetizers, beverages") ||
    lowerResponse.includes("سوشي، ستيك، دجاج، مقبلات، مشروبات")
  ) {
    // Show randomized category suggestions after menu categories mention
    if (isArabic.value) {
      newSuggestions = generateRandomizedMenuSuggestions('ar');
    } else {
      newSuggestions = generateRandomizedMenuSuggestions('en');
    }
    suggestions.value = newSuggestions;
    return;
  }
  
  // Menu-related suggestions
  if (
    lowerMessage.includes("menu") ||
    lowerMessage.includes("food") ||
    lowerMessage.includes("dish") ||
    lowerMessage.includes("قائمة") ||
    lowerMessage.includes("طعام") ||
    lowerMessage.includes("أطباق")
  ) {
    if (isArabic.value) {
      newSuggestions = ["أرني خيارات السوشي", "ما هي أطباق الستيك؟"];
    } else {
      newSuggestions = [
        "Show me your sushi options",
        "What steak dishes do you have?",
      ];
    }
  }

  // Appetizer-related suggestions
  else if (
    lowerMessage.includes("appetizer") ||
    lowerMessage.includes("starter") ||
    lowerResponse.includes("appetizer") ||
    lowerMessage.includes("مقبلات")
  ) {
    if (isArabic.value) {
      newSuggestions = ["أخبرني عن الحساء", "ما هي المقبلات الأخرى؟"];
    } else {
      newSuggestions = [
        "Tell me about your soups",
        "What other appetizers do you have?",
      ];
    }
  }

  // Sushi-related suggestions
  else if (
    lowerMessage.includes("sushi") ||
    lowerResponse.includes("sushi") ||
    lowerMessage.includes("سوشي")
  ) {
    if (isArabic.value) {
      newSuggestions = ["ماذا في سوشي التنين؟", "أرني أطباق الستيك"];
    } else {
      newSuggestions = [
        "What's in the Dragon Sushi?",
        "Show me your steak dishes",
      ];
    }
  }

  // Steak-related suggestions
  else if (
    lowerMessage.includes("steak") ||
    lowerMessage.includes("beef") ||
    lowerResponse.includes("steak") ||
    lowerMessage.includes("ستيك") ||
    lowerMessage.includes("لحم")
  ) {
    if (isArabic.value) {
      newSuggestions = ["ما مميز في لحم واجيو؟", "أرني أطباق الدجاج"];
    } else {
      newSuggestions = [
        "What's special about Wagyu Beef?",
        "Show me your chicken dishes",
      ];
    }
  }

  // Chicken-related suggestions
  else if (
    lowerMessage.includes("chicken") ||
    lowerResponse.includes("chicken") ||
    lowerMessage.includes("دجاج")
  ) {
    if (isArabic.value) {
      newSuggestions = [
        "ماذا في دجاج بارميزان كريمي؟",
        "أرني خيارات السوشي",
      ];
    } else {
      newSuggestions = [
        "What's in the Creamy Parmesan chicken?",
        "Show me your sushi options",
      ];
    }
  }

  // Beverage-related suggestions
  else if (
    lowerMessage.includes("drink") ||
    lowerMessage.includes("beverage") ||
    lowerResponse.includes("beverage") ||
    lowerMessage.includes("مشروب") ||
    lowerMessage.includes("مشروبات")
  ) {
    if (isArabic.value) {
      newSuggestions = ["ما هو فرنشمان بليتز؟", "أرني المقبلات"];
    } else {
      newSuggestions = [
        "What's the Frenchman Blitz?",
        "Show me your appetizers",
      ];
    }
  }

  // Noodle-related suggestions
  else if (
    lowerMessage.includes("noodle") ||
    lowerResponse.includes("noodle") ||
    lowerMessage.includes("نودل")
  ) {
    if (isArabic.value) {
      newSuggestions = ["ماذا في شوربة النودلز؟", "أرني أطباق أخرى"];
    } else {
      newSuggestions = [
        "What's in the Noodle Soup?",
        "Show me other dishes",
      ];
    }
  }

  // Ingredient-related suggestions
  else if (
    lowerMessage.includes("tomato") ||
    lowerResponse.includes("tomato") ||
    lowerMessage.includes("طماطم")
  ) {
    if (isArabic.value) {
      newSuggestions = ["ماذا في توست الطماطم؟", "أرني المقبلات الأخرى"];
    } else {
      newSuggestions = [
        "What's in the Tomato Toast?",
        "Show me other appetizers",
      ];
    }
  }

  else if (
    lowerMessage.includes("salmon") ||
    lowerResponse.includes("salmon") ||
    lowerMessage.includes("سلمون")
  ) {
    if (isArabic.value) {
      newSuggestions = [
        "ماذا في رول سلمون السوشي؟",
        "أرني أطباق بحرية أخرى",
      ];
    } else {
      newSuggestions = [
        "What's in the Roll Salmon Sushi?",
        "Show me other seafood dishes",
      ];
    }
  }

  else if (
    lowerMessage.includes("cheese") ||
    lowerMessage.includes("parmesan") ||
    lowerResponse.includes("parmesan") ||
    lowerMessage.includes("جبن") ||
    lowerMessage.includes("بارميزان")
  ) {
    if (isArabic.value) {
      newSuggestions = [
        "ماذا في دجاج بارميزان كريمي؟",
        "أرني أطباق دجاج أخرى",
      ];
    } else {
      newSuggestions = [
        "What's in the Creamy Parmesan chicken?",
        "Show me other chicken dishes",
      ];
    }
  }

  else if (
    lowerMessage.includes("pumpkin") ||
    lowerResponse.includes("pumpkin") ||
    lowerMessage.includes("قرع")
  ) {
    if (isArabic.value) {
      newSuggestions = ["ماذا في شوربة القرع؟", "أرني شوربات أخرى"];
    } else {
      newSuggestions = [
        "What's in the Pumpkin Soup?",
        "Show me other soups",
      ];
    }
  }

  // Hours-related suggestions
  else if (
    lowerMessage.includes("hour") ||
    lowerMessage.includes("open") ||
    lowerResponse.includes("hour") ||
    lowerMessage.includes("ساعة") ||
    lowerMessage.includes("مفتوح")
  ) {
    if (isArabic.value) {
      newSuggestions = ["أين موقعكم؟", "كيف يمكنني الحجز؟"];
    } else {
      newSuggestions = ["Where are you located?", "How can I make a reservation?"];
    }
  }

  // Location-related suggestions
  else if (
    lowerMessage.includes("address") ||
    lowerMessage.includes("location") ||
    lowerResponse.includes("address") ||
    lowerMessage.includes("عنوان") ||
    lowerMessage.includes("موقع")
  ) {
    if (isArabic.value) {
      newSuggestions = ["ما هي ساعات العمل؟", "كيف يمكنني الحجز؟"];
    } else {
      newSuggestions = ["What are your hours?", "How can I make a reservation?"];
    }
  }

  // Contact-related suggestions
  else if (
    lowerMessage.includes("phone") ||
    lowerMessage.includes("call") ||
    lowerResponse.includes("phone") ||
    lowerMessage.includes("هاتف") ||
    lowerMessage.includes("اتصل")
  ) {
    if (isArabic.value) {
      newSuggestions = ["أين موقعكم؟", "ما هي ساعات العمل؟"];
    } else {
      newSuggestions = ["Where are you located?", "What are your hours?"];
    }
  }

  // Delivery-related suggestions
  else if (
    lowerMessage.includes("delivery") ||
    lowerMessage.includes("deliver") ||
    lowerResponse.includes("delivery") ||
    lowerMessage.includes("توصيل") ||
    lowerMessage.includes("يوصلون")
  ) {
    if (isArabic.value) {
      newSuggestions = ["كم تكلفة التوصيل؟", "كيف يمكنني الطلب؟"];
    } else {
      newSuggestions = [
        "What's the delivery cost?",
        "How can I place an order?",
      ];
    }
  }

  // Specials-related suggestions
  else if (
    lowerMessage.includes("special") ||
    lowerMessage.includes("specials") ||
    lowerResponse.includes("special") ||
    lowerMessage.includes("عرض") ||
    lowerMessage.includes("عروض")
  ) {
    if (isArabic.value) {
      newSuggestions = ["ما هي أكثر الأطباق شعبية؟", "كيف يمكنني الطلب؟"];
    } else {
      newSuggestions = [
        "What are your most popular dishes?",
        "How can I place an order?",
      ];
    }
  }

  // Recommendation-related suggestions
  else if (
    lowerMessage.includes("recommend") ||
    lowerMessage.includes("popular") ||
    lowerResponse.includes("popular") ||
    lowerMessage.includes("أنصح") ||
    lowerMessage.includes("شائع")
  ) {
    if (isArabic.value) {
      newSuggestions = ["ما هي عروض اليوم؟", "أرني القائمة"];
    } else {
      newSuggestions = ["What are today's specials?", "Show me the menu"];
    }
  }

  // Payment-related suggestions - AVOID REDUNDANT QUESTIONS
  else if (
    lowerMessage.includes("payment") ||
    lowerMessage.includes("pay") ||
    lowerResponse.includes("payment") ||
    lowerMessage.includes("دفع") ||
    lowerMessage.includes("ادفع")
  ) {
    if (isArabic.value) {
      newSuggestions = ["كيف يمكنني الطلب؟", "أرني القائمة"];
    } else {
      newSuggestions = ["How can I place an order?", "Show me the menu"];
    }
  }

  // Reservation-related suggestions
  else if (
    lowerMessage.includes("reservation") ||
    lowerMessage.includes("book") ||
    lowerMessage.includes("حجز") ||
    lowerResponse.includes("reservation")
  ) {
    if (isArabic.value) {
      newSuggestions = ["كم عدد الأشخاص؟", "ما الوقت المناسب؟"];
    } else {
      newSuggestions = ["How many people?", "What time works best?"];
    }
  }

  // Order-related suggestions
  else if (
    lowerMessage.includes("order") ||
    lowerMessage.includes("طلب") ||
    lowerResponse.includes("order")
  ) {
    if (isArabic.value) {
      newSuggestions = ["أرني القائمة", "ما هي عروض اليوم؟"];
    } else {
      newSuggestions = ["Show me the menu", "What are today's specials?"];
    }
  }

  // Price-related suggestions
  else if (
    lowerMessage.includes("price") ||
    lowerMessage.includes("cost") ||
    lowerMessage.includes("سعر") ||
    lowerMessage.includes("تكلفة")
  ) {
    if (isArabic.value) {
      newSuggestions = ["أرني القائمة", "ما هي عروض اليوم؟"];
    } else {
      newSuggestions = ["Show me the menu", "What are today's specials?"];
    }
  }

  // If no specific context is detected, provide general helpful suggestions
  else {
    if (isArabic.value) {
      newSuggestions = ["أرني القائمة", "كيف يمكنني الحجز؟"];
    } else {
      newSuggestions = ["Show me the menu", "How can I make a reservation?"];
    }
  }

  suggestions.value = newSuggestions;
}

async function getBotResponse(userMessage) {
  try {
    console.log("Getting bot response for message:", userMessage);
    console.log("Current page language:", locale.value);
    console.log("Is Arabic:", isArabic.value);

    // Set enhanced chatbot language
    setEnhancedLanguage(locale.value);

    // First try enhanced chatbot service
    console.log("🤖 Trying enhanced chatbot service...");
    const enhancedResponse = await getEnhancedResponse(
      userMessage,
      locale.value
    );

    if (enhancedResponse) {
      console.log("✅ Enhanced chatbot provided response");
      return enhancedResponse;
    }

    // Fallback to existing smart chatbot
    console.log("📝 Enhanced chatbot returned null, trying smart chatbot...");

    // Use the global instance if available, otherwise fall back to hybrid response
    if (window.smartChatbotInstance) {
      console.log("Using smart chatbot instance");
      // Ensure language is set correctly before generating response
      window.smartChatbotInstance.setPageLanguage(locale.value);
      console.log("Set chatbot language to:", locale.value);
      console.log(
        "Current chatbot language after setting:",
        window.smartChatbotInstance.getCurrentLanguage()
      );

      const response = await window.smartChatbotInstance.generateResponse(
        userMessage
      );
      console.log("Smart chatbot response:", response);
      return response;
    } else {
      console.log("Using hybrid response");
      // Import and use the singleton instance directly
      const { getSmartResponse } = await import("@/utils/smartChatbot.js");
      const response = await getSmartResponse(userMessage, locale.value);
      return response;
    }
  } catch (error) {
    console.error("Error getting bot response:", error);
    if (isArabic.value) {
      return "عذراً، لدي مشكلة في معالجة طلبك الآن. يرجى المحاولة مرة أخرى لاحقاً.";
    }
    return "I'm sorry, I'm having trouble processing your request right now. Please try again later.";
  }
}

async function sendMessage() {
  if (!userInput.value.trim() || loading.value) return;
  const userMessage = userInput.value;
  messages.value.push({ text: userMessage, sender: "user" });
  userInput.value = "";

  // Scroll immediately after adding user message
  scrollToBottom();

  loading.value = true;
  const response = await getBotResponse(userMessage);
  messages.value.push({ text: response, sender: "bot" });
  loading.value = false;
  updateSuggestions(userMessage, response);

  // Scroll again after bot response
  scrollToBottom();

  // Focus on input element after bot response
  nextTick(() => {
    if (inputElement.value) {
      inputElement.value.focus();
    }
  });
}

async function sendSuggestion(suggestion) {
  messages.value.push({ text: suggestion, sender: "user" });

  // Scroll immediately after adding user suggestion
  scrollToBottom();

  loading.value = true;
  const response = await getBotResponse(suggestion);
  messages.value.push({ text: response, sender: "bot" });
  loading.value = false;
  updateSuggestions(suggestion, response);

  // Scroll again after bot response
  scrollToBottom();

  // Focus on input element after bot response
  nextTick(() => {
    if (inputElement.value) {
      inputElement.value.focus();
    }
  });
}

function toggleMenu() {
  showMenu.value = !showMenu.value;
}

// Function to restart conversation and reset flow
async function restartChat() {
  messages.value = [];
  conversationPhase.value = 'initial';
  customerIntent.value = null;
  customerPreferences.value = {};
  
  // Reset conversation context
  conversationContext.value = {
    exploredCategories: [],
    currentFocus: null,
    conversationDepth: 0,
    lastResponse: null,
    menuExplorationPhase: 'initial',
    contextUpdated: false
  };
  
  suggestions.value = getSuggestionsForLanguage();
  showMenu.value = false;
  clearSmartChatbotMemory(); // Clear conversation memory

  // Force refresh data to ensure we have the latest menu
  try {
    await refreshSmartChatbotData();
    await refreshEnhancedData(); // Refresh enhanced chatbot data
    console.log("Data refreshed successfully");
  } catch (error) {
    console.error("Error refreshing data:", error);
  }

  nextTick(scrollToBottom);
}

function scrollToBottom() {
  // Immediate scroll
  if (messagesContainer.value) {
    messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
  }

  // Also scroll after next tick to ensure DOM is updated
  nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
    }
  });
}

function handleClickOutside(event) {
  if (!showMenu.value) return;
  const menuEl = menu.value;
  const btnEl = menuBtn.value;
  if (
    menuEl &&
    !menuEl.contains(event.target) &&
    btnEl &&
    !btnEl.contains(event.target)
  ) {
    showMenu.value = false;
  }
}

// Watch for when chat opens to refresh data
watch(openChat, async (newValue) => {
  if (newValue) {
    // Reset conversation flow for fresh start
    conversationPhase.value = 'initial';
    customerIntent.value = null;
    customerPreferences.value = {};
    
         // Reset conversation context
     conversationContext.value = {
       exploredCategories: [],
       currentFocus: null,
       conversationDepth: 0,
       lastResponse: null,
       menuExplorationPhase: 'initial',
       contextUpdated: false
     };
    
    // Force refresh data when opening chat
    try {
      await refreshSmartChatbotData();
      await refreshEnhancedData(); // Refresh enhanced chatbot data
      console.log("Data refreshed on chat open");

      // Clear conversation memory to ensure fresh start
      clearSmartChatbotMemory();
      console.log("Conversation memory cleared on chat open");
      
      // Update suggestions to match new conversation phase
      suggestions.value = getSuggestionsForLanguage();
      console.log("Conversation flow reset to initial phase");
    } catch (error) {
      console.error("Error refreshing data on chat open:", error);
    }
  }
});

// Watch for language changes and update chatbot language
watch(locale, (newLocale) => {
  console.log("Language changed to:", newLocale);
  // Update the chatbot language when page language changes
  if (window.smartChatbotInstance) {
    window.smartChatbotInstance.setPageLanguage(newLocale);
    // Clear conversation memory when language changes
    window.smartChatbotInstance.clearMemory();
  }
  
  // Reset conversation flow for new language
  conversationPhase.value = 'initial';
  customerIntent.value = null;
  customerPreferences.value = {};
  
     // Reset conversation context for new language
   conversationContext.value = {
     exploredCategories: [],
     currentFocus: null,
     conversationDepth: 0,
     lastResponse: null,
     menuExplorationPhase: 'initial',
     contextUpdated: false
   };
  
  // Update suggestions to match new language and conversation phase
  suggestions.value = getSuggestionsForLanguage();
  // Clear messages when language changes
  messages.value = [];
  
  console.log("Conversation flow reset for new language:", newLocale);
});

onMounted(() => {
  document.addEventListener("mousedown", handleClickOutside);
  checkMobile(); // Set initial mobile state
  window.addEventListener("resize", checkMobile); // Update on resize

  // Set the chatbot language based on current page language
  import("@/utils/smartChatbot.js").then(({ SmartChatbot }) => {
    if (!window.smartChatbotInstance) {
      window.smartChatbotInstance = new SmartChatbot();
    }
    window.smartChatbotInstance.setPageLanguage(locale.value);
    console.log("Chatbot initialized with language:", locale.value);
    console.log("Current locale value:", locale.value);
    console.log("Is Arabic:", isArabic.value);
    console.log(
      "Chatbot instance language:",
      window.smartChatbotInstance.getCurrentLanguage()
    );
  });
});
onBeforeUnmount(() => {
  document.removeEventListener("mousedown", handleClickOutside);
  window.removeEventListener("resize", checkMobile);
});
</script>

<style scoped>
/* CSS Variables for consistent theming */
:root {
  --primary: #F5BE32;
  --primary-200: #F7D06A;
  --bg: #FFFFFF;
  --surface: #F8F9FA;
  --surface-2: #E9ECEF;
  --text: #212529;
  --on-primary: #212529;
  --on-surface: #212529;
  --muted: #6C757D;
  --border: #DEE2E6;
}

/* --- Layout anchors --- */
.chatbot-icon-wrapper {
  position: fixed;
  left: 32px;
  top: 55%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 1000;
}
.chatbot-icon-wrapper.rtl {
  left: auto;
  right: 32px;
}

/* --- Labels --- */
.chatbot-label {
  color: var(--primary);
  text-align: center;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  font-size: 16px;
  font-weight: 400;
  margin-bottom: 8px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  animation: fadeInUp 0.6s ease-out;
}
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* --- Icon button (desktop) --- */
.chatbot-icon {
  width: 70px;
  height: 70px;
  background: linear-gradient(135deg, var(--bg) 0%, var(--surface) 100%);
  border-radius: 50%;
  box-shadow: var(--card-shadow), 0 4px 16px rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 2px solid color-mix(in srgb, var(--primary) 20%, transparent);
  position: relative;
  overflow: hidden;
}
.chatbot-icon::before {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(
    135deg,
    color-mix(in srgb, var(--primary) 10%, transparent) 0%,
    transparent 100%
  );
  border-radius: 50%;
  opacity: 0;
  transition: opacity 0.3s ease;
}
.chatbot-icon:hover {
  transform: translateY(-4px) scale(1.05);
  box-shadow: 0 12px 40px color-mix(in srgb, var(--primary) 40%, transparent),
    0 8px 24px rgba(0, 0, 0, 0.5);
  border-color: color-mix(in srgb, var(--primary) 40%, transparent);
}
.chatbot-icon:hover::before {
  opacity: 1;
}
.chatbot-icon img {
  width: 48px;
  height: 48px;
  object-fit: contain;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
  transition: transform 0.3s ease;
}
.chatbot-icon:hover img {
  transform: scale(1.1);
}

/* --- Floating button (mobile) --- */
.chatbot-mobile-button {
  position: fixed;
  bottom: 20px;
  left: 20px;
  width: 60px;
  height: 60px;
  display: none;
  background: linear-gradient(135deg, var(--bg) 0%, var(--surface) 100%);
  border-radius: 50%;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4), 0 4px 16px rgba(0, 0, 0, 0.3);
  z-index: 1000;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 2px solid color-mix(in srgb, var(--primary) 20%, transparent);
  position: relative;
  overflow: hidden;
}
.chatbot-mobile-button::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: linear-gradient(
    135deg,
    color-mix(in srgb, var(--primary) 10%, transparent) 0%,
    transparent 100%
  );
  opacity: 0;
  transition: opacity 0.3s ease;
}
.chatbot-mobile-button:hover {
  transform: translateY(-4px) scale(1.05);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5), 0 8px 24px rgba(0, 0, 0, 0.4);
  border-color: color-mix(in srgb, var(--primary) 40%, transparent);
}
.chatbot-mobile-button:hover::before {
  opacity: 1;
}
.chatbot-mobile-button img {
  width: 32px;
  height: 32px;
  object-fit: contain;
  z-index: 1;
  position: relative;
  display: block;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
  transition: transform 0.3s ease;
}
.chatbot-mobile-button:hover img {
  transform: scale(1.1);
}

.chatbot-mobile-label {
  position: absolute;
  top: -30px;
  left: 50%;
  transform: translateX(-50%);
  color: var(--primary);
  text-align: center;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  font-size: 14px;
  font-weight: 400;
  white-space: nowrap;
  z-index: 1001;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  animation: fadeInUp 0.6s ease-out 0.2s both;
}
.chatbot-mobile-pulse {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: color-mix(in srgb, var(--primary) 30%, transparent);
  opacity: 0;
  animation: pulse 2s infinite;
}
@keyframes pulse {
  0% {
    transform: scale(0.9);
    opacity: 0.7;
  }
  50% {
    transform: scale(1.1);
    opacity: 0;
  }
  100% {
    transform: scale(0.9);
    opacity: 0.7;
  }
}

.chatbot-desktop-icon {
  display: none;
}

/* --- Popup container --- */
.chatbot-popup {
  position: fixed;
  left: 120px;
  top: 50%;
  transform: translateY(-50%);
  width: 420px;
  height: 600px;
  max-width: 95vw;
  background: linear-gradient(135deg, var(--bg) 0%, var(--surface) 100%);
  border-radius: 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5),
    0 8px 32px color-mix(in srgb, var(--primary) 15%, transparent);
  display: flex;
  flex-direction: column;
  gap: 0;
  z-index: 1100;
  border: 2px solid color-mix(in srgb, var(--primary) 30%, transparent);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  backdrop-filter: blur(10px);
  animation: slideIn 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}
@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-50%) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translateY(-50%) scale(1);
  }
}
.chatbot-popup.rtl {
  left: auto;
  right: 120px;
  direction: rtl;
  text-align: right;
}

.chatbot-popup.mobile-view {
  left: 20px;
  top: 80%;
  transform: none;
  width: 90%;
  height: auto;
  max-width: none;
  border-radius: 20px;
  box-shadow: 0 0 32px 0 color-mix(in srgb, var(--primary) 25%, transparent),
    0 2px 16px 0 rgba(0, 0, 0, 0.25);
  border: 2px solid var(--primary);
  font-size: 16px;
  padding: 10px;
}

/* --- Mobile header --- */
.chatbot-mobile-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: linear-gradient(
    135deg,
    var(--primary-200) 0%,
    var(--primary) 100%
  );
  color: var(--on-primary);
  border-radius: 24px 24px 0 0;
  font-size: 18px;
  font-weight: 700;
  position: relative;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
}
.chatbot-mobile-header-content {
  display: flex;
  align-items: center;
  gap: 12px;
}
.chatbot-mobile-info h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}
.chatbot-status {
  font-size: 13px;
  color: var(--border);
  font-weight: 500;
}

.chatbot-mobile-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.chatbot-mobile-menu-btn,
.chatbot-popup-menu-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  transition: all 0.2s ease;
}
.chatbot-mobile-menu-btn:hover,
.chatbot-popup-menu-btn:hover {
  background: color-mix(in srgb, var(--text) 10%, transparent);
  transform: scale(1.05);
}
.chatbot-mobile-menu {
  position: absolute;
  top: 60px;
  right: 20px;
  background: linear-gradient(135deg, var(--surface-2) 0%, var(--surface) 100%);
  border: 1px solid color-mix(in srgb, var(--primary) 30%, transparent);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  z-index: 1200;
  min-width: 140px;
  padding: 8px 0;
  backdrop-filter: blur(10px);
  animation: fadeIn 0.2s ease-out;
}
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
.chatbot-mobile-menu-item {
  background: none;
  border: none;
  color: var(--primary);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  font-size: 15px;
  padding: 12px 20px;
  width: 100%;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 0;
}
.chatbot-mobile-menu-item:hover {
  background: color-mix(in srgb, var(--primary) 10%, transparent);
  color: var(--primary);
}
.chatbot-mobile-close {
  background: none;
  border: none;
  color: var(--on-primary);
  font-size: 24px;
  font-weight: bold;
  cursor: pointer;
  line-height: 1;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  transition: all 0.2s ease;
}
.chatbot-mobile-close:hover {
  background: color-mix(in srgb, var(--text) 10%, transparent);
  transform: scale(1.05);
}

/* --- Desktop header --- */
.chatbot-popup-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: linear-gradient(
    135deg,
    var(--primary-200) 0%,
    var(--primary) 100%
  );
  color: var(--on-primary);
  border-radius: 24px 24px 0 0;
  padding: 20px 24px;
  font-size: 20px;
  font-weight: 700;
  position: relative;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
}
.chatbot-popup-header-content {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}
.chatbot-popup-header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  white-space: nowrap;   /* don't wrap to next line */
  margin-left: auto;     /* push to the right */
}
.chatbot-popup-info {
  display: flex;
  flex-direction: column;
}
.chatbot-popup-title {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  font-size: 22px;
  font-weight: 700;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  margin: 0;
}
.chatbot-popup-status {
  font-size: 13px;
  color: var(--muted);
  font-weight: 500;
}

.chatbot-popup-menu {
  position: absolute;
  top: 60px;
  right: 24px;
  background: linear-gradient(135deg, var(--surface-2) 0%, var(--surface) 100%);
  border: 1px solid color-mix(in srgb, var(--primary) 30%, transparent);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  z-index: 1200;
  min-width: 140px;
  padding: 8px 0;
  backdrop-filter: blur(10px);
  animation: fadeIn 0.2s ease-out;
}
.chatbot-popup-menu-item {
  background: none;
  border: none;
  color: var(--primary);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  font-size: 15px;
  padding: 12px 20px;
  width: 100%;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 0;
}
.chatbot-popup-menu-item:hover {
  background: color-mix(in srgb, var(--primary) 10%, transparent);
  color: var(--primary);
}

/* --- Body & messages --- */
.chatbot-popup-body {
  padding: 20px 24px 0 24px;
  color: var(--on-surface);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  font-size: 18px;
  background: linear-gradient(135deg, var(--bg) 0%, var(--surface) 100%);
  flex: 1 1 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  margin-bottom: 0;
  border-radius: 0;
  overflow: hidden;
  padding-bottom: 0;
}
.chatbot-popup-messages {
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;
  padding-right: 8px;
  padding-bottom: 0;
  scrollbar-width: thin;
  scrollbar-color: var(--primary) var(--surface);
}
.chatbot-popup-messages::-webkit-scrollbar {
  width: 8px;
  background: var(--surface);
  border-radius: 4px;
}
.chatbot-popup-messages::-webkit-scrollbar-thumb {
  background: linear-gradient(
    135deg,
    var(--primary) 0%,
    var(--primary-200) 100%
  );
  border-radius: 4px;
}
.chatbot-popup-messages::-webkit-scrollbar-button {
  display: none;
}

.chatbot-popup-welcome {
  color: var(--primary);
  text-align: center;
  font-size: 18px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  margin-bottom: 0;
  padding-bottom: 0;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  animation: fadeInUp 0.6s ease-out;
}

.chatbot-popup-welcome .chatbot-welcome-subtitle {
  font-size: 14px;
  color: var(--muted);
  margin-top: 4px;
  text-shadow: none;
}

.chatbot-popup-message {
  background: linear-gradient(135deg, var(--surface-2) 0%, var(--surface) 100%);
  color: var(--on-surface);
  border-radius: 18px;
  padding: 12px 16px;
  max-width: 80%;
  align-self: flex-end;
  font-size: 16px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  border: 1px solid color-mix(in srgb, var(--text) 10%, transparent);
  animation: messageSlideIn 0.3s ease-out;
}
@keyframes messageSlideIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.chatbot-popup-message.user {
  background: linear-gradient(
    135deg,
    var(--primary-200) 0%,
    var(--primary) 100%
  );
  color: var(--on-primary);
  align-self: flex-end;
  box-shadow: 0 4px 12px color-mix(in srgb, var(--primary) 30%, transparent);
}
.chatbot-popup.rtl .chatbot-popup-message.user {
  align-self: flex-start;
}

.chatbot-popup-message.bot {
  background: linear-gradient(135deg, var(--surface) 0%, var(--surface-2) 100%);
  color: var(--on-surface);
  align-self: flex-start;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}
.chatbot-popup.rtl .chatbot-popup-message.bot {
  align-self: flex-end;
}

/* --- Suggestions --- */
.chatbot-popup-suggestions {
  display: flex;
  gap: 12px;
  padding: 20px 24px 12px 24px;
  justify-content: center;
  background: linear-gradient(135deg, var(--bg) 0%, var(--surface) 100%);
  border-top: 1px solid color-mix(in srgb, var(--text) 10%, transparent);
  margin-top: 0;
  flex-wrap: nowrap;
  overflow-x: auto;
  scrollbar-width: none;
}
.chatbot-popup-suggestions::-webkit-scrollbar {
  display: none;
}
.chatbot-popup-suggestion {
  background: linear-gradient(135deg, var(--surface-2) 0%, var(--surface) 100%);
  color: var(--primary);
  border: 1px solid color-mix(in srgb, var(--primary) 30%, transparent);
  border-radius: 20px;
  padding: 8px 16px;
  font-size: 15px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(5px);
}
.chatbot-popup-suggestion:hover {
  background: linear-gradient(
    135deg,
    var(--primary) 0%,
    var(--primary-200) 100%
  );
  color: var(--on-primary);
  transform: translateY(-2px);
  box-shadow: 0 6px 16px color-mix(in srgb, var(--primary) 30%, transparent);
}

/* --- Mobile suggestions --- */
.chatbot-mobile-suggestions {
  display: none;
  padding: 16px 20px 8px 20px;
  background: linear-gradient(135deg, var(--bg) 0%, var(--surface) 100%);
  border-top: 1px solid color-mix(in srgb, var(--text) 10%, transparent);
  margin-top: 0;
}
.chatbot-mobile-suggestions-scroll {
  display: flex;
  gap: 12px;
  overflow-x: auto;
  padding-bottom: 8px;
  scrollbar-width: none;
}
.chatbot-mobile-suggestions-scroll::-webkit-scrollbar {
  display: none;
}
.chatbot-mobile-suggestion {
  background: linear-gradient(135deg, var(--surface-2) 0%, var(--surface) 100%);
  color: var(--primary);
  border: 1px solid color-mix(in srgb, var(--primary) 30%, transparent);
  border-radius: 20px;
  padding: 8px 16px;
  font-size: 14px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  cursor: pointer;
  transition: all 0.3s ease;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(5px);
}
.chatbot-mobile-suggestion:hover {
  background: linear-gradient(
    135deg,
    var(--primary) 0%,
    var(--primary-200) 100%
  );
  color: var(--on-primary);
  transform: translateY(-2px);
  box-shadow: 0 6px 16px color-mix(in srgb, var(--primary) 30%, transparent);
}

/* --- Input bar --- */
.chatbot-popup-input-bar {
  display: flex;
  align-items: center;
  padding: 20px 24px 24px 24px;
  background: linear-gradient(135deg, var(--bg) 0%, var(--surface) 100%);
  border-radius: 0 0 24px 24px;
  border-top: 1px solid color-mix(in srgb, var(--text) 10%, transparent);
}
.chatbot-popup-input {
  flex: 1;
  padding: 14px 18px;
  border-radius: 24px;
  border: 2px solid color-mix(in srgb, var(--primary) 30%, transparent);
  background: linear-gradient(135deg, var(--surface-2) 0%, var(--primary) 100%);
  color: var(--on-surface);
  font-size: 16px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  outline: none;
  margin-right: 12px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(5px);
}
.chatbot-popup-input:focus {
  border-color: color-mix(in srgb, var(--primary) 60%, transparent);
  box-shadow: 0 4px 16px color-mix(in srgb, var(--primary) 20%, transparent);
  transform: translateY(-1px);
}
.chatbot-popup.rtl .chatbot-popup-input {
  margin-right: 0;
  margin-left: 12px;
}

/* --- Send button --- */
.chatbot-popup-send {
  background: linear-gradient(135deg, var(--surface-2) 0%, var(--surface) 100%);
  border: 2px solid color-mix(in srgb, var(--primary) 30%, transparent);
  border-radius: 50%;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(5px);
}
.chatbot-popup-send:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}
.chatbot-popup-send:hover:not(:disabled) {
  background: linear-gradient(
    135deg,
    var(--primary) 0%,
    var(--primary-200) 100%
  );
  transform: translateY(-2px) scale(1.05);
  box-shadow: 0 6px 16px color-mix(in srgb, var(--primary) 30%, transparent);
}
.chatbot-popup-send svg {
  display: block;
  transition: transform 0.3s ease;
}
.chatbot-popup-send:hover:not(:disabled) svg {
  transform: scale(1.1);
}

/* --- Typing indicator --- */
.typing-indicator {
  background: linear-gradient(
    135deg,
    var(--surface) 0%,
    var(--surface-2) 100%
  ) !important;
  color: var(--primary) !important;
  align-self: flex-start;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}
.typing-dots {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 0;
}
.typing-dots span {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: linear-gradient(
    135deg,
    var(--primary) 0%,
    var(--primary-200) 100%
  );
  animation: typing 1.4s infinite ease-in-out;
  box-shadow: 0 2px 4px color-mix(in srgb, var(--primary) 30%, transparent);
}
.typing-dots span:nth-child(1) {
  animation-delay: -0.32s;
}
.typing-dots span:nth-child(2) {
  animation-delay: -0.16s;
}
.typing-dots span:nth-child(3) {
  animation-delay: 0s;
}
@keyframes typing {
  0%,
  80%,
  100% {
    transform: scale(0.8);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

/* --- Mobile tweaks --- */
@media (max-width: 768px) {
  .chatbot-icon-wrapper {
    position: fixed;
    left: auto;
    right: auto;
    top: auto;
    bottom: 20px;
    transform: none;
    z-index: 1000;
  }
  .chatbot-desktop-icon {
    display: none;
  }
  .chatbot-mobile-button {
    display: flex !important;
    position: fixed !important;
    bottom: 20px !important;
    left: 20px !important;
    width: 60px !important;
    height: 60px !important;
    background: linear-gradient(
      135deg,
      var(--bg) 0%,
      var(--surface) 100%
    ) !important;
    border-radius: 50% !important;
    z-index: 1000 !important;
    cursor: pointer !important;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3) !important;
  }
  .chatbot-mobile-button img {
    width: 32px !important;
    height: 32px !important;
    object-fit: contain !important;
    z-index: 1 !important;
    position: relative !important;
    display: block !important;
  }

  .chatbot-popup.mobile-view {
    position: fixed;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    max-width: none;
    border-radius: 0;
    transform: none;
    z-index: 1100;
    display: flex;
    flex-direction: column;
    animation: slideUp 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  }
  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(100%);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .chatbot-popup-body {
    flex: 1;
    padding: 10px 15px 0 15px;
    font-size: 16px;
  }
  .chatbot-popup-messages {
    padding-right: 2px;
  }
  .chatbot-popup-message {
    max-width: 85%;
    font-size: 15px;
    padding: 10px 12px;
  }
  .chatbot-popup-input-bar {
    padding: 15px;
    border-top: 1px solid var(--border);
  }
  .chatbot-popup-input {
    font-size: 16px;
    padding: 12px 16px;
  }

  .chatbot-mobile-suggestions {
    display: block;
    padding: 10px 15px;
  }
  .chatbot-mobile-header {
    display: flex;
  }
  .chatbot-popup-header {
    display: none;
  }
}

/* --- Desktop visibility --- */
@media (min-width: 769px) {
  .chatbot-mobile-button {
    display: none;
  }
  .chatbot-desktop-icon {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  .chatbot-mobile-header {
    display: none;
  }
  .chatbot-mobile-suggestions {
    display: none;
  }
  .chatbot-popup-header {
    display: flex;
  }
}

/* --- Mobile overlay --- */
.chatbot-mobile-overlay {
  position: fixed;
  inset: 0;
  background: var(--overlay-scrim);
  z-index: 1050;
}

/* --- RTL (mobile) --- */
.chatbot-icon-wrapper.rtl .chatbot-mobile-button {
  left: auto;
  right: 20px;
}
.chatbot-popup.mobile-view.rtl {
  direction: rtl;
  text-align: right;
}
.chatbot-popup.mobile-view.rtl .chatbot-popup-input {
  margin-right: 0;
  margin-left: 10px;
}

/* --- Touch sizes --- */
@media (max-width: 768px) {
  .chatbot-popup-send {
    width: 44px;
    height: 44px;
    min-width: 44px;
  }
  .chatbot-popup-suggestion,
  .chatbot-mobile-suggestion {
    min-height: 44px;
    padding: 8px 16px;
    font-size: 14px;
  }
  .chatbot-mobile-close,
  .chatbot-mobile-menu-btn {
    min-width: 44px;
    min-height: 44px;
  }
  .chatbot-mobile-menu-item {
    min-height: 44px;
    padding: 12px 18px;
  }
}

/* --- Smooth mobile animation --- */
.chatbot-popup.mobile-view {
  animation: slideUp 0.3s ease-out;
}
@keyframes slideUp {
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

/* --- Extra small --- */
@media (max-width: 480px) {
  .chatbot-popup.mobile-view {
    padding: 5px;
  }
  .chatbot-popup-body {
    padding: 8px 10px 0 10px;
  }
  .chatbot-popup-input-bar {
    padding: 10px;
  }
  .chatbot-mobile-suggestions {
    padding: 8px 10px;
  }
  .chatbot-popup-message {
    max-width: 90%;
    font-size: 14px;
    padding: 8px 10px;
  }
}

/* --- Landscape mobile --- */
@media (max-width: 768px) and (orientation: landscape) {
  .chatbot-popup.mobile-view {
    top: 10%;
    bottom: 10%;
    height: 80%;
  }
  .chatbot-popup-body {
    flex: 1;
    min-height: 0;
  }
}

/* --- High DPI --- */
@media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
  .chatbot-mobile-button img,
  .chatbot-icon img {
    image-rendering: -webkit-optimize-contrast;
    image-rendering: crisp-edges;
  }
}

/* --- Reduced motion --- */
@media (prefers-reduced-motion: reduce) {
  .chatbot-mobile-pulse,
  .chatbot-popup.mobile-view,
  .typing-dots span {
    animation: none;
  }
}

/* --- Optional dark preference nudge (kept tokenized) --- */
@media (prefers-color-scheme: dark) {
  .chatbot-mobile-button {
    background: var(--primary);
  }
}

/* --- Focus outlines --- */
.chatbot-mobile-button:focus,
.chatbot-popup-close:focus,
.chatbot-popup-send:focus,
.chatbot-popup-suggestion:focus,
.chatbot-mobile-suggestion:focus {
  outline: 2px solid var(--primary);
  outline-offset: 2px;
}

/* --- No text selection on buttons --- */
.chatbot-mobile-button,
.chatbot-popup-close,
.chatbot-popup-send,
.chatbot-popup-suggestion,
.chatbot-mobile-suggestion {
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}
</style>
